// The page's one loop and its screens (login, worlds, characters, game).
import { Application, Container } from 'pixi.js';
import type { Sprites } from './assets';
import type { Audio } from './audio';
import { setUiScale, UiRoot, focus } from './ui/compose';
import { updateTooltip, hideTip } from './ui/tooltips';
import { settings } from './settings';
import { refreshContent } from './api';
import { arrivals } from './core/session';
import { displayDensity, setDisplayDensity, setTextDensity } from './ui/scale';
import { touchMode, onScreenKeyboard } from './ui/touch';
import { reloadForUpdate } from './clientUpdate';

export interface Screen {
  /** Builds its Pixi UI widgets (app.ui) and world objects (app.scene). */
  enter(): void;
  frame(dt: number): void;
  exit(): void;
  resize?(): void;
}

class App {
  pixi!: Application;
  /** What screens draw into (cleared on every screen change). */
  scene = new Container();
  /** The menu backdrop, kept across screens. */
  backLayer = new Container();
  ui = new UiRoot();
  private pointer = { x: -1, y: -1 };
  sprites!: Sprites;
  audio!: Audio;
  time = 0;
  private screen: Screen | null = null;
  private last = performance.now();

  async init(stage: HTMLElement): Promise<void> {
    this.pixi = new Application();
    setDisplayDensity(window.devicePixelRatio);
    setTextDensity(settings.textDensity);
    await this.pixi.init({ resizeTo: stage, background: '#0a0608', antialias: false, roundPixels: true, autoDensity: true, resolution: displayDensity });
    stage.append(this.pixi.canvas);
    this.pixi.stage.addChild(this.backLayer, this.scene, this.ui);
    this.ui.install();
    focus.install(this.pixi.canvas);
    touchMode.install();
    onScreenKeyboard.install();
    window.addEventListener('pointermove', e => { this.pointer = { x: e.clientX, y: e.clientY }; });
    this.pixi.canvas.addEventListener('contextmenu', e => e.preventDefault());
    this.pixi.canvas.addEventListener('wheel', e => e.preventDefault(), { passive: false });
    this.pixi.ticker.stop(); // our loop drives rendering
    window.addEventListener('resize', () => {
      setUiScale(settings.uiScale, settings.textSize);
      this.screen?.resize?.();
    });
    setUiScale(settings.uiScale, settings.textSize);
    requestAnimationFrame(t => this.loop(t));
    this.keepAliveWhenHidden();
  }

  show(next: Screen): void {
    if (reloadForUpdate()) return; // leaving a world with a new client version waiting
    this.screen?.exit();
    hideTip();
    this.ui.clear();
    this.scene.removeChildren().forEach(c => c.destroy({ children: true }));
    this.screen = next;
    next.enter();
  }

  private loop(t: number): void {
    this.step(t, true);
    requestAnimationFrame(n => this.loop(n));
  }

  private step(t: number, render: boolean): void {
    if (t - this.last > 5000) console.warn(`The page was stopped for ${((t - this.last) / 1000).toFixed(1)} s (tab ${document.visibilityState}).`);
    const dt = Math.min(0.25, Math.max(0, (t - this.last) / 1000));
    this.last = t;
    this.time += dt;
    try {
      // Monitor changes can alter density without changing the CSS viewport size.
      if (render && window.devicePixelRatio !== displayDensity) {
        setDisplayDensity(window.devicePixelRatio);
        this.pixi.renderer.resize(this.width, this.height, displayDensity);
      }
      refreshContent();
      this.screen?.frame(dt);
      this.audio?.update(dt);
      if (render) {
        this.ui.resize(this.width, this.height);
        this.ui.layout();
        updateTooltip(this.pointer.x, this.pointer.y);
        this.ui.layout();
        this.ui.tick(t);
        this.ui.collect();
        if (focus.current && !focus.current.isConnected) focus.set(null);
        this.pixi.renderer.render(this.pixi.stage);
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Browsers stop animation frames in background tabs, which would stop the session's pings and the world would drop
   * it (20 s of silence). While frames aren't coming, the loop runs without drawing whenever a message arrives (about
   * 30 a second in the game) and on a worker's timer (joining, character select): a worker, since the page's own
   * timers slow to once a minute in a tab hidden for long.
   */
  private keepAliveWhenHidden(): void {
    const tick = () => {
      const now = performance.now();
      if (now - this.last > 250) this.step(now, false);
    };
    arrivals.onArrival = tick;
    try {
      const code = 'setInterval(() => postMessage(0), 250);';
      const worker = new Worker(URL.createObjectURL(new Blob([code], { type: 'text/javascript' })));
      worker.onmessage = tick;
    } catch (e) {
      console.warn('No background timer worker:', e);
      setInterval(tick, 250);
    }
    document.addEventListener('visibilitychange', () => console.info(`Tab ${document.visibilityState}.`));
    document.addEventListener('freeze', () => console.warn('The browser froze the page.'));
    document.addEventListener('resume', () => console.info('The browser resumed the page.'));
  }

  get width(): number { return this.pixi.screen.width; }
  get height(): number { return this.pixi.screen.height; }
}

export const app = new App();
